import React, { useState, useEffect } from 'react';

const PathDisplayComponent = ({ path, imageWidth, imageHeight, scale, geoToPixel, debug }) => {
  const [pixelPath, setPixelPath] = useState([]);

  useEffect(() => {
    if (typeof geoToPixel === 'function' && path && path.length > 0) {
      let segmentCount = 0;
      const convertedPath = path.map((segment) => {
        segmentCount += 1;
        return {
          ...segment,
          waypoints: segment.waypoints.map(p => geoToPixel(p.x, p.y)),
          lineNumber: segmentCount
        };
      });
      setPixelPath(convertedPath);
    } else {
      setPixelPath([]);
    }
  }, [geoToPixel, path]);

  if (!path || path.length === 0 || !geoToPixel) return null;

  const arrowSize = Math.max(36, 128 / scale);
  const arrowPath = `M -${arrowSize/2} -${arrowSize/6} L ${arrowSize/2} 0 L -${arrowSize/2} ${arrowSize/6} Z`;
  const textSize = Math.max(36, 64 / scale); // Adjust text size based on scale

  return (
    <svg width={imageWidth} height={imageHeight} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
      {pixelPath.map((segment, segmentIndex) => {
        const points = segment.waypoints;

        // Color mapping for different path types
        const getColor = () => {
          const fwd = segment.direction === 1;
          switch (segment.type) {
            case 'reeds_shepp':
              return fwd ? '#1e88e7' : '#cc0001';           // Blue / Red
            case 'straight':
              return fwd ? '#00e676' : '#ff3bfd';           // Green / Magenta
            case 'straight_pre_turn':
              return fwd ? '#ff9800' : '#e65100';           // Orange / Dark Orange
            case 'straight_pre_straight':
              return fwd ? '#9c27b0' : '#6a1b9a';           // Purple / Dark Purple
            case 'reeds_shepp_post_turn':
              return fwd ? '#00bcd4' : '#006064';           // Cyan / Dark Cyan
            default:
              return '#000000';
          }
        };
        const color = getColor();

        // Stroke width mapping
        const getStrokeWidth = () => {
          const baseWidth = Math.max(10, 50 / scale);
          const widerWidth = Math.max(10, 75 / scale);
          switch (segment.type) {
            case 'reeds_shepp':
            case 'reeds_shepp_post_turn':
              return segment.direction === 1 ? baseWidth : widerWidth;
            case 'straight':
            case 'straight_pre_turn':
            case 'straight_pre_straight':
              return segment.direction === 1 ? baseWidth : widerWidth;
            default:
              return Math.max(1, 50 / scale);
          }
        };
        const strokeWidth = getStrokeWidth();

        // Dash pattern for backward segments
        const getStrokeDasharray = () => {
          if (segment.direction === -1) {
            if (['straight', 'straight_pre_turn', 'straight_pre_straight'].includes(segment.type)) {
              return '50, 20';
            }
          }
          return '0';
        };
        const strokeDasharray = getStrokeDasharray();

        // Opacity mapping - higher for turn-related segments
        const getStrokeOpacity = () => {
          switch (segment.type) {
            case 'reeds_shepp':
            case 'reeds_shepp_post_turn':
              return 1;
            case 'straight_pre_turn':
            case 'straight_pre_straight':
              return 0.8;
            case 'straight':
              return 0.5;
            default:
              return 0.5;
          }
        };
        const strokeOpacity = getStrokeOpacity();
        
        const midIndex = Math.floor(points.length / 2);
        const midPoint = points[midIndex];

        const nextPoint = points[Math.min(midIndex + 1, points.length - 1)];
        const prevPoint = points[Math.max(midIndex - 1, 0)];
        const dx = nextPoint[0] - prevPoint[0];
        const dy = nextPoint[1] - prevPoint[1];
        const angle = Math.atan2(dy, dx) * 180 / Math.PI;

        return (
          <g key={segmentIndex}>
            <path
              d={
                points
                  .filter((_, index) => index % 1 === 0) // only use every 10th point
                  .reduce((acc, [x, y], idx, arr) => {
                    if (idx === 0) return `M ${x} ${y}`;
                    const prev = arr[idx - 1];
                    const midX = (prev[0] + x) / 2;
                    const midY = (prev[1] + y) / 2;
                    return `${acc} Q ${prev[0]} ${prev[1]} ${midX} ${midY}`;
                  }, '') + ` T ${points.slice(-1)[0][0]} ${points.slice(-1)[0][1]}`
              }
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeOpacity={strokeOpacity}
              strokeDasharray={strokeDasharray}
            />
            {debug && points.filter((_, index) => index % 1 === 0).map((point, idx) => (
              <circle key={idx} cx={point[0]} cy={point[1]} r={textSize / 20} fill={'yellow'} />
            ))}
            {debug && <path 
              d={arrowPath}
              fill={color}
              stroke={'black'}
              // strokeWidth={strokeWidth}
              transform={`translate(${midPoint[0]-arrowSize/20},${midPoint[1]-arrowSize/20}) rotate(${
                ['reeds_shepp', 'reeds_shepp_post_turn'].includes(segment.type) ? (segment.direction === -1 ? angle + 180 : angle) : angle
              })`}
            />}
            {segment.lineNumber && debug && (
              <text
                x={midPoint[0]}
                y={midPoint[1]}
                fill="white"
                fontSize={textSize * 1.25}
                stroke="black"
                strokeWidth={0.5}
                fontWeight="bold"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {segment.lineNumber}, {segment.direction === 1 ? "F" : "B"}, {
                  segment.type === 'straight' ? 'S' :
                  segment.type === 'reeds_shepp' ? 'RS' :
                  segment.type === 'straight_pre_turn' ? 'SPT' :
                  segment.type === 'straight_pre_straight' ? 'SPS' :
                  segment.type === 'reeds_shepp_post_turn' ? 'RSPT' : segment.type
                }
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

export default PathDisplayComponent;